/**
 * CompanyEditUserPage Component
 *
 * This component provides a form for editing user details within a company. It allows the user to
 * update various fields such as name, email, and role. The component uses React hooks for state
 * management and form handling.
 *
 * Props:
 * - user: The user object containing details to be edited.
 * - onSave: Function to handle saving the updated user details.
 * - onCancel: Function to handle canceling the edit operation.
 *
 * Example usage:
 * <CompanyEditUserPage
 *   user={user}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 * />
 */

import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UserContext from "../../../../context/UserContext";
import { getUserPublicData } from "../../../../api/userApi";
import {
  softDeleteCompanyUser,
  updateCompanyUser,
} from "../../../../api/companySettingsApi";
import { getAllCompanies } from "../../../../api/settingsCompanyApi";
import { Form, Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import USER_ROLES from "../../../../constants/userRoles";
import { UserDeleteOutlined } from "../../../../helpers/icons/antDesignIcons";
import "./CompanyEditUserPage.scss";
import useTranslation from "../../../../hooks/useTranslation";

const CompanyEditUserPage = () => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation("settingsPage");
  const { userId } = useParams();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [userData, setUserData] = useState(null);
  const [errors, setErrors] = useState({});
  const userRoles = [
    { display: t("superadmin"), value: "0" },
    { display: t("admin"), value: "1" },
    { display: t("location_leader"), value: "2" },
    { display: t("user"), value: "4" },
    { display: t("reseller"), value: "5" },
  ];
  const userStatuses = [
    { display: t("active"), value: "A" },
    { display: t("inactive"), value: "I" },
    { display: t("pending"), value: "P" },
  ];

  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const handleDeleteUserModalClose = () => setShowDeleteUserModal(false);
  const handleDeleteUserModalShow = () => setShowDeleteUserModal(true);

  useEffect(() => {
    if (!userData || userData === null) {
      // Fetch user info if not passed via state
      const fetchUserInfo = async () => {
        try {
          const response = await getUserPublicData(userId);
          setUserData(response);
        } catch (error) {
          console.error("Error fetching user info:", error);
        }
      };
      fetchUserInfo();
    }
  }, [userData, userId]);

  useEffect(() => {
    setUserData(userData);
  }, [userData]);

  // If user is super admin, fetch companies as well with id and name
  useEffect(() => {
    if (user.roles !== USER_ROLES.SUPER_ADMIN) return;

    const fetchCompanies = async () => {
      try {
        const response = await getAllCompanies();
        setCompanies(response);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, [user.roles]);

  if (!userData) {
    return <div>Loading...</div>;
  }

  const handleInputChange = (event) => {
    const { name, type, checked, value } = event.target;
    setUserData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSoftDeleteUser = async (event, id) => {
    event.preventDefault();
    console.log("Deleting user with ID:", id);
    // Here you can handle the user deletion, e.g. send a request to a backend
    const result =  await softDeleteCompanyUser(id);

    if (result.status !== 200) {
      toast.error(t("modify_user_data_error"));
      return;
    }

    toast.success(t("delete_user_success"));
    setShowDeleteUserModal(false);
    navigate("/settings/company");
  };

  console.log("USER IN COMPANYEDITUSER PAGE", user);
  const validate = () => {
    const errors = {};
    if (!userData.lastname)
      errors.lastname = t("update_user_lastname_error_invalid");
    if (userData.lastname && userData.lastname.length < 3)
      errors.lastname = t("update_user_lastname_error_invalid");
    if (userData.lastname && userData.lastname.length > 50)
      errors.lastname = t("update_user_lastname_error_invalid");
    if (!userData.firstname)
      errors.firstname = t("update_user_firstname_error_invalid");
    if (userData.firstname && userData.firstname.length < 3)
      errors.firstname = t("update_user_firstname_error_invalid");
    if (userData.firstname && userData.firstname.length > 50)
      errors.firstname = t("update_user_firstname_error_invalid");
    if (!userData.email) {
      errors.email = t("Email mező kitöltése kötelező");
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(userData.email)
    ) {
      errors.email = t("update_user_email_error_invalid");
    }
    if (!userData.phonenumber) {
      errors.phonenumber = t("update_user_phone_error_invalid");
    } else if (!/^\+?[0-9]{10,15}$/.test(userData.phonenumber)) {
      errors.phonenumber = t("update_user_phone_error_invalid");
    }
    if (!userData.roles) errors.roles = "Szerepkör mező kitöltése kötelező";
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Object.values(validationErrors).forEach((error) => {
        toast.error(error);
      });
      return;
    }

    try {
      // Here you can handle the form submission, e.g. send the data to a backend
      const { _id, ...updatedFields } = userData;
      const response = await updateCompanyUser(_id, updatedFields);

      // If response is successful, update the userData state
      if (response.status !== 200) {
        toast.error(t("modify_user_data_error"));
        return;
      }

      setUserData((prevData) => ({ ...prevData, ...response.data }));
      // Show success message
      toast.success(t("modify_user_data_success"));
      navigate("/settings/company");
    } catch (error) {
      toast.error(t("modify_user_data_error"));
      console.error("Error updating user data:", error);
    }
  };

  const handleBackButton = () => {
    navigate("/settings/company");
  };

  return (
    <div className="container company-edit-user-page__container navbar-margin">
      <div className="page-title-wrapper">
        <h1 className="page-title">{t("modify_user_data")}</h1>
        <div className="page-title-delete__wrapper">
          <p className="page-title-delete_p">{t("delete_user")}</p>
          <button
            className="page-title-delete__button"
            onClick={handleDeleteUserModalShow}
          >
            <UserDeleteOutlined />
          </button>
        </div>
      </div>
      <Modal
        show={showDeleteUserModal}
        onHide={handleDeleteUserModalClose}
        className="company-edit-user-page__delete-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("delete_user")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("delete_user_message")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleDeleteUserModalClose}
            className="btn btn-secondary"
          >
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={(event) => handleSoftDeleteUser(event, userData._id)}
            className="btn btn-danger"
          >
            {t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="company-edit-user-page__content">
        <Form onSubmit={handleSubmit} className="company-edit-user-page-form">
          {/* If user is super admin, show companyname as well */}
          {user.roles === USER_ROLES.SUPER_ADMIN && (
            <Form.Group controlId="formCompany">
              <Form.Label>{t("company")}</Form.Label>
              <Form.Control
                as="select"
                name="company_id"
                value={userData.company_id || ""}
                onChange={handleInputChange}
              >
                <option value="" disabled>
                  {t("selectCompany")}
                </option>
                {companies.map((company) => (
                  <option key={company.companyid} value={company.companyid}>
                    {company.companyname}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          )}
          <Form.Group controlId="formLastName">
            <Form.Label>{t("lastname")}</Form.Label>
            <Form.Control
              type="text"
              name="lastname"
              value={userData.lastname || ""}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group controlId="formFirstName">
            <Form.Label>{t("firstname")}</Form.Label>
            <Form.Control
              type="text"
              name="firstname"
              value={userData.firstname}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group controlId="formEmail">
            <Form.Label>{t("email")}</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={userData.email || ""}
              onChange={handleInputChange}
              autoComplete="on"
            />
          </Form.Group>
          <Form.Group controlId="formPhone">
            <Form.Label>{t("phonenumber")}</Form.Label>
            <Form.Control
              type="tel"
              name="phonenumber"
              value={userData.phonenumber || ""}
              onChange={handleInputChange}
              autoComplete="on"
              /*               placeholder="+36301234567" */
            />
          </Form.Group>
          <Form.Group controlId="formRole">
            <Form.Label>{t("role")}</Form.Label>
            {user.roles === USER_ROLES.COMPANY_ADMIN &&
            userData.roles === USER_ROLES.SUPER_ADMIN ? (
              // If the logged-in user is a company admin and the user being edited is a superadmin, show a non-editable field
              <Form.Control
                as="input"
                readOnly
                value={
                  userRoles.find((role) => role.value === userData.roles)
                    ?.display || ""
                }
              />
            ) : // If the logged-in user is an admin and the user being edited is also a companyadmin, show a non-editable field
            user.roles === USER_ROLES.COMPANY_ADMIN &&
              userData.roles === USER_ROLES.COMPANY_ADMIN ? (
              <Form.Control
                as="input"
                readOnly
                value={
                  userRoles.find((status) => status.value === userData.status)
                    ?.display || ""
                }
              />
            ) : (
              // Otherwise, show the dropdown for role selection
              <Form.Control
                as="select"
                name="roles"
                value={userData.roles || ""}
                onChange={handleInputChange}
              >
                {/* If user is a superadmin, show all roles */}
                {user.roles === USER_ROLES.SUPER_ADMIN &&
                  userRoles.map((roles, index) => (
                    <option key={index} value={roles.value}>
                      {roles.display}
                    </option>
                  ))}

                {/* If user is a company admin, restrict roles */}
                {user.roles === USER_ROLES.COMPANY_ADMIN &&
                  userRoles
                    .filter(
                      (role) =>
                        role.value !== USER_ROLES.SUPER_ADMIN &&
                        role.value !== USER_ROLES.COMPANY_LOCATION_LEADER &&
                        role.value !== USER_ROLES.RESELLER
                    )
                    .map((roles, index) => (
                      <option key={index} value={roles.value}>
                        {roles.display}
                      </option>
                    ))}
              </Form.Control>
            )}
          </Form.Group>

          {/*           <Form.Group controlId="formLocation">
            <Form.Label>{t("site")}</Form.Label>
            <Form.Control
              type="text"
              name="location"
              value={userData.location || ""}
              onChange={handleInputChange}
            />
          </Form.Group> */}
          <Form.Group controlId="formStatus">
            <Form.Label>{t("status")}</Form.Label>
            {user.roles === USER_ROLES.COMPANY_ADMIN &&
            userData.roles === USER_ROLES.SUPER_ADMIN ? (
              // If the logged-in user is a company admin and the user being edited is a superadmin, show a non-editable field
              <Form.Control
                as="input"
                readOnly
                value={
                  userStatuses.find(
                    (status) => status.value === userData.status
                  )?.display || ""
                }
              />
            ) : user.roles === USER_ROLES.COMPANY_ADMIN &&
              userData.roles === USER_ROLES.COMPANY_ADMIN ? (
              // If the logged-in user is a company admin and the user being edited is also a companyadmin, show a non-editable field
              <Form.Control
                as="input"
                readOnly
                value={
                  userStatuses.find(
                    (status) => status.value === userData.status
                  )?.display || ""
                }
              />
            ) : (
              // Show the dropdown for status selection
              <Form.Control
                as="select"
                name="status"
                value={userData.status || ""}
                onChange={handleInputChange}
              >
                {userStatuses.map((status, index) => (
                  <option key={index} value={status.value}>
                    {status.display}
                  </option>
                ))}
              </Form.Control>
            )}
          </Form.Group>
          <Form.Group controlId="formNotifications">
            <Form.Check
              type="checkbox"
              name="emailnotification"
              checked={userData.emailnotification || ""}
              onChange={handleInputChange}
              label={t("email_notification")}
            />
          </Form.Group>
          <Form.Group controlId="formEmailReport">
            <Form.Check
              type="checkbox"
              name="emailreport"
              checked={userData.emailreport || ""}
              onChange={handleInputChange}
              label={t("email_report")}
            />
          </Form.Group>
          <div className="company-edit-user-page__button__wrapper">
            <Button
              type="submit"
              form="company-edit-user-page-form"
              className="btn btn-save company-edit-user-page__save-button"
              onClick={handleSubmit}
            >
              {t("save")}
            </Button>
            <Button
              className="btn btn-secondary company-edit-user-back-button"
              onClick={handleBackButton}
            >
              {t("cancel")}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CompanyEditUserPage;
